class DeviceStorageService {
  constructor() {
    this.storageKey = 'ez_control_saved_devices'
    this.maxSavedDevices = 10
  }

  // Get all saved devices from localStorage
  getSavedDevices() {
    try {
      const saved = localStorage.getItem(this.storageKey)
      if (saved) {
        const devices = JSON.parse(saved)
        return Array.isArray(devices) ? devices : []
      }
      return []
    } catch (error) {
      console.error('Error reading saved devices:', error)
      return []
    }
  }

  // Save a device to localStorage
  saveDevice(device) {
    try {
      const devices = this.getSavedDevices()
      
      // Check if device already exists
      const existingIndex = devices.findIndex(d => d.ip === device.ip)
      
      if (existingIndex >= 0) {
        // Update existing device
        devices[existingIndex] = {
          ...devices[existingIndex],
          ...device,
          lastConnected: new Date().toISOString()
        }
      } else {
        // Add new device
        const deviceToSave = {
          ...device,
          lastConnected: new Date().toISOString(),
          savedAt: new Date().toISOString()
        }
        devices.unshift(deviceToSave) // Add to beginning
        
        // Limit the number of saved devices
        if (devices.length > this.maxSavedDevices) {
          devices.splice(this.maxSavedDevices)
        }
      }
      
      localStorage.setItem(this.storageKey, JSON.stringify(devices))
      console.log('✅ Device saved to localStorage:', device.name)
      return true
    } catch (error) {
      console.error('Error saving device:', error)
      return false
    }
  }

  // Remove a device from localStorage
  removeDevice(deviceIP) {
    try {
      const devices = this.getSavedDevices()
      const filteredDevices = devices.filter(d => d.ip !== deviceIP)
      
      if (filteredDevices.length !== devices.length) {
        localStorage.setItem(this.storageKey, JSON.stringify(filteredDevices))
        console.log('✅ Device removed from localStorage:', deviceIP)
        return true
      }
      return false
    } catch (error) {
      console.error('Error removing device:', error)
      return false
    }
  }

  // Update device connection status
  updateDeviceStatus(deviceIP, status) {
    try {
      const devices = this.getSavedDevices()
      const deviceIndex = devices.findIndex(d => d.ip === deviceIP)
      
      if (deviceIndex >= 0) {
        devices[deviceIndex] = {
          ...devices[deviceIndex],
          status: status,
          lastConnected: new Date().toISOString()
        }
        localStorage.setItem(this.storageKey, JSON.stringify(devices))
        return true
      }
      return false
    } catch (error) {
      console.error('Error updating device status:', error)
      return false
    }
  }

  // Get device by IP
  getDeviceByIP(deviceIP) {
    try {
      const devices = this.getSavedDevices()
      return devices.find(d => d.ip === deviceIP) || null
    } catch (error) {
      console.error('Error getting device by IP:', error)
      return null
    }
  }

  // Clear all saved devices
  clearAllDevices() {
    try {
      localStorage.removeItem(this.storageKey)
      console.log('✅ All saved devices cleared')
      return true
    } catch (error) {
      console.error('Error clearing devices:', error)
      return false
    }
  }

  // Get recently connected devices (last 7 days)
  getRecentDevices() {
    try {
      const devices = this.getSavedDevices()
      const sevenDaysAgo = new Date()
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
      
      return devices.filter(device => {
        const lastConnected = new Date(device.lastConnected)
        return lastConnected > sevenDaysAgo
      })
    } catch (error) {
      console.error('Error getting recent devices:', error)
      return []
    }
  }

  // Export devices data
  exportDevices() {
    try {
      const devices = this.getSavedDevices()
      const dataStr = JSON.stringify(devices, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      
      const link = document.createElement('a')
      link.href = URL.createObjectURL(dataBlob)
      link.download = `ez_control_devices_${new Date().toISOString().split('T')[0]}.json`
      link.click()
      
      return true
    } catch (error) {
      console.error('Error exporting devices:', error)
      return false
    }
  }

  // Import devices data
  importDevices(file) {
    return new Promise((resolve, reject) => {
      try {
        const reader = new FileReader()
        reader.onload = (e) => {
          try {
            const devices = JSON.parse(e.target.result)
            if (Array.isArray(devices)) {
              // Validate devices structure
              const validDevices = devices.filter(device => 
                device.ip && device.name && device.port
              )
              
              if (validDevices.length > 0) {
                // Merge with existing devices
                const existingDevices = this.getSavedDevices()
                const mergedDevices = [...existingDevices]
                
                validDevices.forEach(device => {
                  const existingIndex = mergedDevices.findIndex(d => d.ip === device.ip)
                  if (existingIndex >= 0) {
                    mergedDevices[existingIndex] = { ...mergedDevices[existingIndex], ...device }
                  } else {
                    mergedDevices.push(device)
                  }
                })
                
                // Limit to max devices
                if (mergedDevices.length > this.maxSavedDevices) {
                  mergedDevices.splice(this.maxSavedDevices)
                }
                
                localStorage.setItem(this.storageKey, JSON.stringify(mergedDevices))
                console.log('✅ Devices imported successfully')
                resolve(mergedDevices.length)
              } else {
                reject(new Error('No valid devices found in file'))
              }
            } else {
              reject(new Error('Invalid file format'))
            }
          } catch (parseError) {
            reject(new Error('Failed to parse file'))
          }
        }
        reader.onerror = () => reject(new Error('Failed to read file'))
        reader.readAsText(file)
      } catch (error) {
        reject(error)
      }
    })
  }

  // Get storage statistics
  getStorageStats() {
    try {
      const devices = this.getSavedDevices()
      const totalSize = JSON.stringify(devices).length
      
      return {
        totalDevices: devices.length,
        maxDevices: this.maxSavedDevices,
        storageSize: totalSize,
        storageSizeKB: (totalSize / 1024).toFixed(2),
        lastUpdated: devices.length > 0 ? devices[0].lastConnected : null
      }
    } catch (error) {
      console.error('Error getting storage stats:', error)
      return {
        totalDevices: 0,
        maxDevices: this.maxSavedDevices,
        storageSize: 0,
        storageSizeKB: '0.00',
        lastUpdated: null
      }
    }
  }
}

export const deviceStorageService = new DeviceStorageService() 